import React, { memo, useRef, useEffect } from 'react';
import KolegaInlineLoading from '../inline-loading/KolegaInlineLoading';
import { MAX_TEXTAREA_HEIGHT, MIN_TEXTAREA_HEIGHT } from '../constants';

/**
 * Message input textarea component
 * Auto-resizes based on content with min/max height constraints
 */
const MessageInput = memo(({ 
  input, 
  setInput, 
  isFocused, 
  setIsFocused,
  isEventConnected, 
  agentStatus,
  isSandboxReady,
  uploadedFiles,
  handleChatSubmit,
  availableHeight,
  readOnly = false
}) => {
  const textareaRef = useRef(null);

  const handleChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isDisabled) {
        handleChatSubmit(e);
      }
    }
  };

  const isDisabled = readOnly || !isEventConnected || agentStatus === 'generating' || !isSandboxReady;
  
  const showPlaceholder = input.trim() === '' && !isFocused && agentStatus !== 'generating';
  
  const placeholderText = readOnly
    ? 'View-only mode: upgrade to chat with the agent'
    : !isEventConnected
    ? 'Connecting to agent...'
    : !isSandboxReady
      ? 'Preparing sandbox...'
      : uploadedFiles.length > 0
        ? 'Add a message or press send to analyze the image(s)...'
        : 'Type your message... (Shift+Enter for new line)';

  return (
    <div className="relative w-full h-full">
      {showPlaceholder && (
        <div className="absolute left-4 right-4 top-0 bottom-0 flex items-center justify-start text-neutral-500 dark:text-neutral-400 pointer-events-none text-center px-2">
          {placeholderText}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={input}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        placeholder=""
        className="w-full h-full resize-none focus:outline-none p-2 landscape:p-1 sm:p-4 text-ui-text-primary bg-transparent border-0 textarea-scrollbar"
        disabled={isDisabled}
        data-tour="message-input"
        style={{
          overflowY: 'auto',
        }}
      />

      {agentStatus === 'generating' && (
        <div className="absolute left-2 sm:left-4 top-0 h-full flex items-center pointer-events-none">
          <KolegaInlineLoading 
            isVisible={true}
          />
        </div>
      )}
    </div>
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput;